<template>
  <main class="main page-inside">
    <div class="main-wrapper">
      <section class="section product-list-section">
        <div class="wrapper filters-and-products-wrapper">
          <div class="filter-top-wrapper">
            <div class="left-side">
              <span class="title cs2-title small">{{ $t("CS2 market") }}</span>
            </div>
            <div class="right-side">
              <div class="dropdown">
                <div
                  class="dropdown-header"
                  ref="dropdownRef"
                  @click="isOpen = !isOpen"
                >
                  {{ selectedOptionText }}
                  <div class="arrow-wrapper">
                    <img
                      src="@/assets/img/arrow-down.svg"
                      alt="Arrow"
                      class="arrow"
                      :class="{ rotated: isOpen }"
                    />
                  </div>
                </div>
                <div class="dropdown-options" v-show="isOpen">
                  <div
                    class="dropdown-option"
                    @click="selectSortOption('desc')"
                  >
                    {{ $t("Price: High to Low") }}
                  </div>
                  <div class="dropdown-option" @click="selectSortOption('asc')">
                    {{ $t("Price: Low to High") }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="filters-and-products">
            <div :class="['products-container', { active: !filtersIsVisible }]">
              <div v-if="isLoading" class="loading">
                {{ $t("Loading...") }}
              </div>
              <div v-else-if="error" class="empty">
                <span>{{ error }}</span>
                <button @click="fetchProducts">{{ $t("Retry") }}</button>
              </div>
              <div v-else-if="productList.length > 0" class="list products">
                <div class="item" v-for="item in productList" :key="item.id">
                  <ProductCard
                    :chosenItem="chosenItem"
                    :item="item"
                    :currencySymbol="currencySymbol"
                    :is-listing-page="true"
                    @goToProduct="goToProduct"
                    @addToCart="addToCart"
                  />
                </div>
              </div>
              <div v-else class="empty">
                {{ $t("No products found") }}
              </div>
            </div>

            <div :class="['top', { active: filtersIsVisible }]">
              <div class="filters">
                <div class="left-side">
                  <span class="title cs2-title small">{{ $t("Filters") }}</span>
                  <button
                    class="desc small reset-link text-yellow"
                    @click="resetFilters"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clip-path="url(#clip0_12_903)">
                        <mask
                          id="mask0_12_903"
                          style="mask-type: luminance"
                          maskUnits="userSpaceOnUse"
                          x="0"
                          y="0"
                          width="14"
                          height="14"
                        >
                          <path d="M0 0H14V14H0V0Z" fill="white" />
                        </mask>
                        <g mask="url(#mask0_12_903)">
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M2.0415 7C2.0415 4.61642 4.06288 2.625 6.63082 2.625C7.94554 2.625 9.12416 3.15068 9.95763 3.98657C10.223 4.2528 10.4529 4.55 10.6411 4.87118L9.81465 4.68753C9.34291 4.5827 8.87549 4.88013 8.77066 5.35187C8.66584 5.82362 8.96328 6.29102 9.43502 6.39584L12.06 6.97918C12.4857 7.07379 12.916 6.84052 13.0691 6.43224L13.9441 4.09893C14.1138 3.64645 13.8846 3.14208 13.4321 2.9724C12.9796 2.80272 12.4752 3.03197 12.3055 3.48446L12.1304 3.95157C11.8699 3.5137 11.5556 3.11078 11.1969 2.75096C10.041 1.59166 8.41885 0.875 6.63082 0.875C3.16303 0.875 0.291504 3.58459 0.291504 7C0.291504 10.4154 3.16303 13.125 6.63082 13.125C8.24362 13.125 9.72173 12.5418 10.8442 11.5766C11.4423 11.0623 11.9406 10.4385 12.3043 9.73531C12.5263 9.30609 12.3583 8.77817 11.9291 8.55616C11.4998 8.33414 10.9719 8.50208 10.7499 8.93136C10.4918 9.43028 10.1356 9.87787 9.70318 10.2497C8.89211 10.9472 7.81697 11.375 6.63082 11.375C4.06288 11.375 2.0415 9.38356 2.0415 7Z"
                            fill="white"
                            fill-opacity="0.5"
                          />
                        </g>
                      </g>
                      <defs>
                        <clipPath id="clip0_12_903">
                          <rect width="14" height="14" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </button>
                </div>
                <div v-if="isFiltersLoading" class="loading">
                  {{ $t("Loading filters...") }}
                </div>
                <div class="filters-wrapper" v-else>
                  <RangeSlider
                    :max-bar-value="PRICE_CEILING"
                    :min-value="activeFilters.price_from"
                    :max-value="activeFilters.price_till"
                    @update-range="updatePriceFilter"
                  />
                  <div
                    v-for="(filter, filterName) in visibleFilterOptions"
                    :key="filterName"
                    class="filter-container"
                    :class="`filter-${filterName}-container`"
                  >
                    <div
                      :class="[
                        'title small',
                        { active: !expandedFilters.has(filterName) },
                      ]"
                      @click="toggleFilterVisibility(filterName)"
                    >
                      {{ $t(filter.translationKey) }}
                    </div>
                    <div
                      :class="[
                        'select-container',
                        { hidden: !expandedFilters.has(filterName) },
                      ]"
                    >
                      <label
                        class="checkbox-label"
                        v-for="option in filter.options"
                        :key="option"
                      >
                        <div class="checkbox-label-wrapper">
                          <input
                            type="radio"
                            :name="filterName"
                            :value="option"
                            :checked="activeFilters[filterName] === option"
                            @change="updateFilter(filterName, option)"
                          />
                          <div class="checkbox"></div>
                          <span class="title">{{ $t(option) }}</span>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Pagination
            v-if="totalProducts > 0 && !isLoading"
            :total-pages="totalPages"
            :per-page="activeFilters.limit"
            :current-page="activeFilters.page"
            @update:per-page="updatePerPage"
            @update:current-page="changePage"
          />
        </div>
      </section>
    </div>
  </main>
</template>

<script>
import ProductCard from "../components/ProductCard.vue";
import Pagination from "../components/PaginationComponent.vue";
import RangeSlider from "@/components/RangeSlider.vue";
import { mapGetters } from "vuex";
import { isEqual, debounce } from "lodash";
import { onClickOutside } from "@vueuse/core";

const PRICE_CEILING = 9999.99;
const DEFAULT_LIMIT = 20;

const DEFAULT_FILTERS = Object.freeze({
  sort: "desc",
  page: 1,
  limit: DEFAULT_LIMIT,
  price_from: 0.0,
  price_till: PRICE_CEILING,
  types: "all",
  qualities: "all",
  heroes: "all",
  exterior_names: "all",
  classes: "all",
  special_offers: "all",
  search: "",
});

const FILTER_CONFIG = Object.freeze({
  types: { apiParam: "type", translationKey: "Types" },
  qualities: { apiParam: "quality", translationKey: "Qualities" },
  heroes: { apiParam: "hero", translationKey: "Heroes" },
  exterior_names: {
    apiParam: "exterior_names",
    translationKey: "Exterior Names",
  },
  classes: { apiParam: "item_class", translationKey: "Classes" },
  special_offers: {
    apiParam: "special_offers",
    translationKey: "Special Offers",
  },
});

export default {
  name: "ProductListPage",
  components: { Pagination, ProductCard, RangeSlider },
  props: {
    chosenItem: String,
    type: String,
    itemType: String,
  },
  data() {
    return {
      PRICE_CEILING,
      productList: [],
      totalProducts: 0,
      totalPages: 1,
      isLoading: true,
      isFiltersLoading: true,
      error: null,
      filtersIsVisible: true,
      isOpen: false,
      filterOptions: {},
      activeFilters: { ...DEFAULT_FILTERS },
      expandedFilters: new Set(["types"]),
    };
  },
  computed: {
    ...mapGetters("app", ["currencySymbol", "currencyCode"]),
    selectedOptionText() {
      return this.activeFilters.sort === "desc"
        ? this.$t("Price: High to Low")
        : this.$t("Price: Low to High");
    },
    visibleFilterOptions() {
      const result = {};
      for (const key in this.filterOptions) {
        if (Object.prototype.hasOwnProperty.call(this.filterOptions, key)) {
          const filter = this.filterOptions[key];
          if (filter && filter.options && filter.options.length > 1) {
            result[key] = filter;
          }
        }
      }
      return result;
    },
  },
  watch: {
    "$route.query": {
      deep: true,
      handler(newQuery, oldQuery) {
        if (!isEqual(newQuery, oldQuery)) {
          this.syncStateFromUrl(newQuery);
          this.fetchProducts();
        }
      },
    },
    "$route.params.itemType": {
      handler(newType, oldType) {
        if (newType !== oldType) {
          this.initializePage();
        }
      },
    },
    currencyCode(newCode, oldCode) {
      if (newCode !== oldCode) {
        this.fetchProducts();
      }
    },
  },
  created() {
    this.updateAndFetchDebounced = debounce(this.updateAndFetch, 400);
    this.initializePage();
  },
  mounted() {
    onClickOutside(this.$refs.dropdownRef, () => {
      this.isOpen = false;
    });
  },
  methods: {
    async initializePage() {
      this.isLoading = true;
      this.isFiltersLoading = true;
      this.error = null;

      this.syncStateFromUrl(this.$route.query);

      try {
        await Promise.all([this.fetchFilterOptions(), this.fetchProducts()]);
      } catch (err) {
        console.error("Initialization failed:", err);
        this.error = this.$t("Error loading page data");
        this.isLoading = false;
        this.isFiltersLoading = false;
      }
    },

    syncStateFromUrl(query) {
      const filters = { ...DEFAULT_FILTERS };
      filters.page = Math.max(1, parseInt(query.page, 10) || 1);
      filters.limit = parseInt(query.limit, 10) || DEFAULT_LIMIT;
      filters.sort = ["asc", "desc"].includes(query.sort)
        ? query.sort
        : DEFAULT_FILTERS.sort;
      filters.price_from = Math.max(0, parseFloat(query.price_from) || 0);
      filters.price_till = Math.min(
        PRICE_CEILING,
        parseFloat(query.price_till) || PRICE_CEILING,
      );
      filters.search = query.search || DEFAULT_FILTERS.search;

      const newExpandedFilters = new Set(["types"]);
      for (const key in FILTER_CONFIG) {
        if (query[key] && query[key] !== "all") {
          filters[key] = query[key];
          newExpandedFilters.add(key);
        }
      }
      this.activeFilters = filters;
      this.expandedFilters = newExpandedFilters;
    },

    getCleanQuery(filters) {
      const query = {};
      for (const key in filters) {
        if (
          filters[key] !== DEFAULT_FILTERS[key] &&
          filters[key] !== null &&
          filters[key] !== undefined
        ) {
          query[key] = String(filters[key]);
        }
      }
      return query;
    },

    updateUrl() {
      const query = this.getCleanQuery(this.activeFilters);
      if (!isEqual(this.$route.query, query)) {
        this.$router.push({ query }).catch((err) => {
          if (err.name !== "NavigationDuplicated") console.error(err);
        });
      }
    },

    updateAndFetch(newPartialFilters) {
      const newFilters = { ...this.activeFilters, ...newPartialFilters };
      // Reset page to 1 if any filter except pagination itself is changed
      if (!("page" in newPartialFilters)) {
        newFilters.page = 1;
      }

      this.activeFilters = newFilters;
      this.updateUrl();
      // The watcher will handle the fetch call after the URL is updated
    },

    buildApiParams() {
      const params = new URLSearchParams();
      const category = this.$route.params.itemType;
      if (category && category !== "all") params.append("category", category);

      const {
        page,
        limit,
        sort,
        price_from,
        price_till,
        search,
        ...restFilters
      } = this.activeFilters;

      params.append("page", page);
      params.append("limit", limit);
      params.append("sort", sort);
      params.append("price_from", price_from);
      if (price_till <= PRICE_CEILING) {
        params.append("price_till", price_till);
      }
      if (search) params.append("search", search);

      for (const key in restFilters) {
        const value = restFilters[key];
        const config = FILTER_CONFIG[key];
        if (config && value && value !== "all") {
          if (key === "special_offers" && value === "sale") {
            params.append("sale", true);
          } else {
            params.append(config.apiParam, value);
          }
        }
      }
      return params;
    },

    async fetchProducts() {
      this.isLoading = true;
      this.error = null;
      try {
        const params = this.buildApiParams();
        const res = await this.$http.get(
          `${process.env.VUE_APP_API}items/list?${params.toString()}`,
        );
        this.productList = res.data.payload || [];
        this.totalProducts = res.data.meta.total || 0;
        this.totalPages = res.data.meta.pages || 1;
      } catch (error) {
        this.error = this.$t("Error loading products");
        this.productList = [];
        this.totalProducts = 0;
        console.error("Error fetching products:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchFilterOptions() {
      this.isFiltersLoading = true;
      try {
        const params = new URLSearchParams();
        const category = this.$route.params.itemType;
        if (category && category !== "all") params.append("category", category);

        const res = await this.$http.get(
          `${process.env.VUE_APP_API}items/filter-data?${params.toString()}`,
        );
        const data = res.data.payload;
        const newFilterOptions = {};

        for (const key in FILTER_CONFIG) {
          const config = FILTER_CONFIG[key];
          const options = data[key] || [];
          const sanitizedOptions = [
            ...new Set(
              options
                .filter((o) => o && String(o).trim())
                .map((o) => String(o).toLowerCase()),
            ),
          ];

          newFilterOptions[key] = {
            ...config,
            options: ["all", ...sanitizedOptions],
          };
        }

        if (data.special_offers && Array.isArray(data.special_offers)) {
          newFilterOptions.special_offers.options = [
            "all",
            ...new Set(data.special_offers),
          ];
        } else {
          newFilterOptions.special_offers.options = ["all", "sale"];
        }
        this.filterOptions = newFilterOptions;
      } catch (error) {
        console.error("Error fetching filter options:", error);
        this.error = this.$t("Error loading filters");
      } finally {
        this.isFiltersLoading = false;
      }
    },

    resetFilters() {
      if (isEqual(this.getCleanQuery(this.activeFilters), {})) return;
      this.updateAndFetch(DEFAULT_FILTERS);
    },
    selectSortOption(sortValue) {
      this.isOpen = false;
      this.updateAndFetch({ sort: sortValue });
    },
    updatePriceFilter(range) {
      this.updateAndFetchDebounced({
        price_from: range.min,
        price_till: range.max,
      });
    },
    updateFilter(filterName, value) {
      this.updateAndFetch({ [filterName]: value });
    },
    changePage(page) {
      this.updateAndFetch({ page });
    },
    updatePerPage(newPerPage) {
      this.updateAndFetch({ limit: newPerPage });
    },
    toggleFilterVisibility(filterName) {
      const newSet = new Set(this.expandedFilters);
      if (newSet.has(filterName)) {
        newSet.delete(filterName);
      } else {
        newSet.add(filterName);
      }
      this.expandedFilters = newSet;
    },
    back() {
      this.$router.go(-1);
    },
    addToCart(item) {
      this.$emit("addToCart", item);
    },
    goToProduct(item) {
      this.$emit("goToProduct", item);
    },
  },
};
</script>

<style lang="scss" scoped>
.product-list-section {
  color: #fff;
}

.empty {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.empty button {
  background: #233ce5;
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: bold;
}

.section .filters-and-products-wrapper {
  max-width: 1420px;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  padding: 0 0 40px;
}

.top-block {
  position: relative;

  .title-top {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  .main-title {
    width: 100%;
    max-width: 1420px;
    margin: 52px auto;
    display: flex;
    align-items: center;
    gap: 10px;

    button {
      line-height: 0;
    }
  }

  .cs2-title {
    font-size: 16px;
  }

  .reset-link {
    font-size: 12px;
    color: #686a7f;
  }
}
.filter-top-wrapper {
  position: relative;
  width: 100%;
  background: #191919;
  border-radius: 12px;
  padding: 22px 22px 22px 36px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: space-between;

  .left-side {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;

    .cs2-title {
      display: block;
    }
  }

  .right-side {
    position: relative;
    z-index: 2;
  }
}

.top-block .wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1420px;
  padding: 12px 0 12px 24px;
  flex-wrap: wrap;
}

.filter-top-wrapper .dropdown {
  position: relative;
  display: inline-block;

  .dropdown-header {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 12px;
    background: #2b2b2b;
    color: #fff;
    font-weight: 400;
    font-size: 14px;
    padding: 12px;
    border-radius: 6px;
  }

  .arrow-wrapper {
    width: 10px;
    height: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .arrow {
    width: 12px;
    height: 12px;
    transition: transform 0.3s ease;
  }

  .arrow.rotated {
    transform: rotate(180deg);
  }

  .dropdown-options {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    border: 1px solid #44445a;
    background-color: #2f3436;
    z-index: 10;
  }

  .dropdown-option {
    padding: 10px;
    cursor: pointer;
  }

  .dropdown-option:hover {
    background-color: #fff;
    color: #2f3436;
  }
}

.products-container {
  flex: 1;
  transition: 0.3s;
}

.products-container.active {
  width: 100%;
}

.product-list-section .top .wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.filters-and-products {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  flex-direction: row-reverse;
  gap: 40px;
  width: 100%;
  margin-top: 16px;
  gap: 16px;
}

.filters {
  background: #191919;
  padding: 24px 20px;
  border-radius: 12px;

  .left-side {
    display: flex;
    gap: 16px;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }
  .filters-wrapper {
    margin-top: 40px;
  }
}
.filters .title {
  font-size: 14px;
  line-height: 17px;
  font-weight: 700;
  cursor: pointer;
  position: relative;
  text-transform: uppercase;

  &::after {
    content: "";
    position: absolute;
    right: 5px;
    top: 50%;
    transform: translateY(-50%);
    width: 12px;
    height: 12px;
    background-image: url("../../src/assets/img/plus.svg");
    background-size: 12px;
    background-position: center;
    background-repeat: no-repeat;
    transition: 0.3s;
  }

  &.active::after {
    background-image: url("../../src/assets/img/minus.svg");
  }
}

.filter-container {
  margin-top: 38px;
  margin-bottom: 38px;
}

.filters .select-container {
  margin-top: 24px;
  transition: 0.3s;
  overflow: auto;
  max-height: 305px;
}

.filters .select-container.hidden {
  max-height: 0;
  margin-top: 0;
  overflow: hidden;
}

.filters .select-container-reset {
  padding-bottom: 0;
  margin-top: 20px;
  display: table;
  width: 100%;

  .button {
    background: #15ca5c;
    width: 100%;
    border-radius: 12px;
  }

  &::before {
    display: none;
  }
}

.filters .select-container-reset .reset {
  justify-content: center;
  width: 126px;
}

.filters .select-container-range {
  max-height: 67px;
}

.product-list-section .checkbox-label .title {
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  font-family: "Roboto", sans-serif;
  padding: 0;
  text-transform: capitalize;

  &::after {
    display: none;
  }
}

.filters-wrapper > div {
  position: relative;
}

.reset-button-frame {
  padding: 3px;
  max-width: 230px;
  margin-bottom: 37px;
  border: 1px solid #ffffff1a;
}

.reset-button {
  background: #ffffff1a;
  color: #686a7f;
  font-weight: 700;
  font-size: 12px;

  &:hover {
    background: #15ca5c1a;
    color: #15ca5c;
  }
}

.filters-price-container {
  margin-top: 37px;
  margin-bottom: 34px;
}

.list.products {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(208px, 1fr));
  justify-content: center;
  gap: 6px;
}
.reset-link {
  display: flex;
  gap: 12px;
  align-items: center;
}

@media screen and (max-width: 768px) {
  .filters .select-container {
    max-height: 500px;
  }

  .filters-and-products {
    padding-left: 0;
  }
  .top-block .main-title {
    margin: 24px auto;
  }
  .top-block .title-top {
    position: static;
    transform: none;
    margin-bottom: 32px;
  }
}
</style>
